import * as React from 'react';
import { Table } from 'react-bootstrap';

import DownEquipmentBadge from 'af-root/scenes/Company/ScheduleBoard/Shared/DownEquipmentBadge';
import PreviousFieldReportPreviewFRTModal from 'af-root/scenes/Company/ScheduleBoard/Shared/OrderInfo/FieldReportTypePreviewModal';
import PreviousFieldReportsSection from 'af-root/scenes/Company/ScheduleBoard/Shared/OrderInfoModal/Sections/PreviousFieldReportsSection';

import type { W_WorkOrder_FindPrivateConfirmation_VM } from 'ab-api/web/workOrder/findPrivateConfirmation';
import type { W_WorkOrder_FindPublicConfirmation_VM } from 'ab-api/web/workOrder/findPublicConfirmation';

import type FieldReportTypePreviewInModalVM from 'ab-viewModels/fieldReport/fieldReportTypeSidePanel.viewModel';

import { prettyBytes } from '@acceligentllc/shared/utils/formatting';
import { capitalizeText } from '@acceligentllc/shared/utils/text';
import { formatDate } from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { bemElement } from 'af-utils/bem.util';
import { getColorTextClass, getColorBackgroundClass } from 'af-utils/color.util';

import DisabledFeatures from 'ab-common/environment/disabledFeatures';

import { SegmentLabel } from '@acceligentllc/storybook';

import LabelWithColor from 'af-components/LabelWithColor';

interface OwnProps {
	workOrder: W_WorkOrder_FindPrivateConfirmation_VM | W_WorkOrder_FindPublicConfirmation_VM;
}

type Props = OwnProps;

const getResourceClassName = (resource: OwnProps['workOrder']['workOrderResources'][0]): string => {
	let className = 'confirmation-preview__resource-content ';
	if (resource.type === 'LABOR' || resource.type === 'TEMPORARY LABOR') {
		className += getColorTextClass(resource.color);
	} else if (resource.type === 'EQUIPMENT') {
		className += getColorBackgroundClass(resource.color);
	}
	className += resource.noCDL ? ' confirmation-preview__no-cdl' : '';
	return className;
};

const renderResources = (resource: OwnProps['workOrder']['workOrderResources'][0], index: number) => {
	const { name, description, isAvailable, type } = resource;

	return (
		<div className={getResourceClassName(resource)} key={`resource-${index}`}>
			{type === 'TEMPORARY LABOR' &&
				<span className="icon-temp_labor sb-resource-item__temp-labor-icon" />
			}
			<span>{capitalizeText(`${name} ${description}`)}</span>
			{!isAvailable && <DownEquipmentBadge />}
		</div>
	);
};

const WorkOrderConfirmation: React.FC<Props> = ({ workOrder }) => {

	const [fieldReportTypeToPreview, setFieldReportTypeToPreview] = React.useState<Nullable<FieldReportTypePreviewInModalVM>>(null);
	const [fieldReportTypePreviewHeaderValue, setFieldReportTypePreviewHeaderValue] = React.useState<Nullable<string>>(null);

	const openFieldReportTypePreviewModal = React.useCallback((fetchedFieldReportType: FieldReportTypePreviewInModalVM, _headerValue: string) => {
		setFieldReportTypeToPreview(fetchedFieldReportType);
		setFieldReportTypePreviewHeaderValue(_headerValue);
	}, []);

	const closeFieldReportTypePreviewModal = React.useCallback(() => {
		setFieldReportTypeToPreview(null);
		setFieldReportTypePreviewHeaderValue(null);
	}, []);

	if (!workOrder) {
		return null;
	}

	const {
		code,
		crewNumber,
		crewTypeColor,
		crewTypeName,
		dueDate,
		jobCustomerCompany,
		jobDivision,
		jobNote,
		jobOffice,
		jobTitle,
		jobTravelLocationShort,
		jobAddressNotes,
		projectManagerName,
		projectManagerPhoneNumber,
		revision,
		statusBasedRevision,
		scopeOfWork,
		shift,
		siteContactCompanyName,
		siteContactEmails,
		siteContactName,
		siteContactPhoneNumbers,
		siteContactRole,
		siteContactStreet,
		supervisorName,
		supervisorPhoneNumber,
		workEnd,
		workLocations,
		workOrderAttachmentsFromDirectories,
		workOrderNotes,
		workOrderResources,
		workStart,
		deliverableCodeStandard,
		deliverableFileFormat,
		deliverableSoftware,
		isCancelled,
		isPaused,
		cancellationReason,
		pauseReason,
		updatedAt,
		updatedBy,
		hasPreviousFieldReportTypes,
	} = workOrder;

	const headerClassName = bemElement('confirmation-preview', 'header', {
		canceled: isCancelled,
		paused: isPaused,
	});

	const showHeaderReason = (isCancelled || isPaused);

	return (
		<>
			<div className="confirmation-preview">
				<div className="confirmation-preview__general">
					<div className={headerClassName}>
						<div className="confirmation-preview__header__title-container">
							<div className="confirmation-preview__header__status">
								{isPaused && <span className="icon-pause status-icon text-white" />}
								{isCancelled && <span className="icon-close status-icon bg-white text-red" />}
								<div className="confirmation-preview__order-info">WORK ORDER</div>
							</div>
							<div className="confirmation-preview__order-info">{code} CR {crewNumber} {revision ? `REV ${revision}` : ''}{statusBasedRevision ? `.${statusBasedRevision}` : ''}</div>
						</div>
						{showHeaderReason &&
							<div className="confirmation-preview__header__reason">
								<div>{cancellationReason ?? pauseReason}</div>
								<div className="confirmation-preview__header__date">{formatDate(updatedAt, TimeFormat.DAY_WITH_DATE, TimeFormat.FULL_DATE)} by {updatedBy}</div>
							</div>
						}
					</div>
					<div className="confirmation-preview__general-content confirmation-preview__general-content--centered bg-grey">
						<div className="confirmation-preview__order-info">
							{projectManagerName &&
								<div>
									<div>{projectManagerName}/Proj Mgr</div>
									<div>{projectManagerPhoneNumber}</div>
								</div>
							}
							<div>
								<div>{supervisorName}/Supt</div>
								<div>{supervisorPhoneNumber}</div>
							</div>
						</div>
						{
							!!crewTypeColor && !!crewTypeName &&
							<div className="confirmation-preview__order-info">
								<LabelWithColor color={crewTypeColor} text={crewTypeName} />
							</div>
						}
						<div className="confirmation-preview__order-info">
							<div>{dueDate}</div>
							<div>{shift}</div>
							<div>{workStart} - {workEnd}</div>
						</div>
					</div>
					<div className="confirmation-preview__general-content confirmation-preview__general-content--centered">
						<div className="confirmation-preview__job-info">
							<div>{jobCustomerCompany}</div>
							<div>{jobTitle}</div>
							<div>{jobTravelLocationShort}</div>
							{jobAddressNotes &&
								<div>{jobAddressNotes}</div>
							}
						</div>
						<div className="confirmation-preview__job-info">
							<div>{jobOffice}</div>
							<div>{jobDivision}</div>
						</div>
					</div>
				</div>
				<div className="confirmation-preview__general">
					<div className="confirmation-preview__general-content">
						<div className="confirmation-preview__work-locations">
							<div className="m-t-s m-b-s">
								<SegmentLabel
									label="Work Locations"
									tooltip="The location where work is being performed for this Work Order."
								/>
							</div>
							{
								workLocations?.length
									? workLocations.map((l, i) => (
										<div className="confirmation-preview__location" key={`work-location-${i}`}>
											<div>{l.street}</div>
											{l.notes &&
												<div>
													{l.notes}
												</div>
											}
											<div>
												{l.mapLink ?
													<a className="map-link" href={l.mapLink}>View on Google Maps</a> :
													<span>Google Maps link is not available</span>
												}
											</div>
										</div>
									))
									: <div>None</div>
							}
						</div>
						<div className="confirmation-preview__site-contact">
							<div className="m-t-s m-b-s">
								<SegmentLabel label="Site Contact" />
							</div>
							{
								siteContactName
									? <>
										<div>{siteContactName}, {siteContactRole}</div>
										<div>{siteContactCompanyName}</div>
										<div>{siteContactStreet}</div>
										<div>{siteContactEmails.map((e, i) => <div key={`${e}#${i}`}>{e}</div>)}</div>
										<div>{siteContactPhoneNumbers.map((pn, i) => <div key={`${pn}#${i}`}>{pn}</div>)}</div>
									</>
									: <div>N/A</div>
							}
						</div>
					</div>
				</div>

				{deliverableSoftware &&
					<div className="confirmation-preview__general">
						<div className="confirmation-preview__general-content">
							<div className="confirmation-preview__deliverables">
								<div className="confirmation-preview__deliverables-content">
									<div className="m-t-s m-b-s">
										<SegmentLabel label="Deliverable Software" />
									</div>
									{deliverableSoftware}
								</div>
								<div className="confirmation-preview__deliverables-content">
									<div className="m-t-s m-b-s">
										<SegmentLabel label="Deliverable File Format" />
									</div>
									{deliverableFileFormat}
								</div>
								<div className="confirmation-preview__deliverables-content">
									<div className="m-t-s m-b-s">
										<SegmentLabel label="Deliverable Code Standard" />
									</div>
									{deliverableCodeStandard}
								</div>
							</div>
						</div>
					</div>
				}
				<div className="confirmation-preview__general">
					<div className="confirmation-preview__general-content">
						<div className="confirmation-preview__notes">
							<div className="confirmation-preview__notes-content">
								<div className="m-t-s m-b-s">
									<SegmentLabel label="Work Order Notes" />
								</div>
								{workOrderNotes ?? 'No Note'}
							</div>
							<div className="confirmation-preview__notes-content">
								<div className="m-t-s m-b-s">
									<SegmentLabel label="Scope Of Work" />
								</div>
								{scopeOfWork ?? 'No Note'}
							</div>
						</div>
						<div className="confirmation-preview__notes">
							<div className="confirmation-preview__notes-content">
								<div className="m-t-s m-b-s">
									<SegmentLabel label="Job Notes" />
								</div>
								{jobNote ?? 'No Note'}
							</div>
						</div>
					</div>
				</div>
				<div className="confirmation-preview__resources">
					<div className="m-t-s m-b-s">
						<SegmentLabel label="Labor/Equipment" />
					</div>
					{
						workOrderResources?.length
							? workOrderResources.map(renderResources)
							: <span>No assignments available</span>
					}
				</div>
				{
					workOrderAttachmentsFromDirectories?.length ?
						<div className="confirmation-preview__attachments">
							<div className="confirmation-preview__attachments-label">
								<div className="m-t-s m-b-s">
									<SegmentLabel label="Work Order Attachments" />
								</div>
							</div>
							<Table striped={true}>
								<thead>
									<tr>
										<th>File Name</th>
										<th className="hide-on-mobile">Size</th>
									</tr>
								</thead>
								<tbody>
									{workOrderAttachmentsFromDirectories.map((a, i) => (
										<tr key={`attachment-${i}`}>
											<td>
												<div><a className="btn-link" download={a.name} href={a.url}>{a.name}</a></div>
												<div className="show-on-mobile">{prettyBytes(a.size)}</div>
											</td>
											<td className="hide-on-mobile">{prettyBytes(a.size)}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div> :
						null
				}
				{
					!DisabledFeatures.PreviousFieldReports && hasPreviousFieldReportTypes &&
					<div className="confirmation-preview__attachments">
						<div className="confirmation-preview__previous-frts-label">
							<div className="m-t-s m-b-s">
								<SegmentLabel label="Previous Field Report Types" />
							</div>
						</div>
						<div className="confirmation-preview__previous-frts">
							<PreviousFieldReportsSection
								actionsEnabled={true}
								canDeletePFRT={false}
								onPreviewPFRT={openFieldReportTypePreviewModal}
								showSegmentLabel={false}
								workOrderId={workOrder.id}
							/>
						</div>
					</div>
				}
			</div>
			{!DisabledFeatures.PreviousFieldReports && hasPreviousFieldReportTypes && fieldReportTypeToPreview && fieldReportTypePreviewHeaderValue &&
				<PreviousFieldReportPreviewFRTModal
					fieldReportTypeToPreview={fieldReportTypeToPreview}
					headerValue={fieldReportTypePreviewHeaderValue}
					onClose={closeFieldReportTypePreviewModal}
				/>
			}
		</>
	);
};

export default WorkOrderConfirmation;
